// Shared capture → grade → review controller, reused by "Scan Answer Key" and
// "Scan Papers". Renders a camera view (with a file fallback), grades the still
// in the engine worker, then shows the overlay + a tappable answers grid where
// flagged items can be corrected. Behaviour differs by mode:
//   - 'key':    no student; SAVE is blocked until every flagged item is resolved.
//   - 'papers': match/assign a student, live score vs the key; SAVE always allowed.
import { qs, onClick, toast, esc } from './dom.js';
import { icon } from './icons.js';
import { getStream, stopStream, captureStill, frameToImageData, fileToImageData, drawImageData } from './camera.js';
import { gradeInWorker, detectInWorker, type SheetQuad } from './engineClient.js';
import { scoreResponse } from '../scoring/score.js';
import { answerRowHtml, answerGridHtml } from './answerGrid.js';
import { drawSheetPreview } from './sheetOverlay.js';
import type { RawResponse, LayoutMap } from '../engine/index.js';
import type { AnswerKeyRecord, StudentRecord, IdentificationMode } from '../db/schema.js';

export interface SaveIdentity {
  studentId: string | null;
  capturedName?: string;
  capturedNameImage?: Blob | null;
}

export interface ScanConfig {
  layout: LayoutMap;
  mode: 'key' | 'papers';
  /** 'roster'/'lrn_direct' show the student picker + read LRN; 'captured_name'
   *  shows the name crop + text field (no LRN). */
  identificationMode?: IdentificationMode;
  keyForScoring?: AnswerKeyRecord | null;
  students?: StudentRecord[];
  hint?: string;
  onSave: (raw: RawResponse, identity: SaveIdentity) => void | Promise<void>;
}

const pct = (a: number, b: number) => (b ? Math.round((100 * a) / b) : 0);

/** Convert an engine crop (RGBA) to a PNG Blob for storage/display. */
function imageDataToPngBlob(img: { data: Uint8ClampedArray; width: number; height: number }): Promise<Blob | null> {
  const canvas = document.createElement('canvas');
  drawImageData(canvas, img);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'));
}

export class ScanController {
  private lastImage: ImageData | null = null;
  private camAlive = false;
  private detectTimer = 0;
  private detectBusy = false;
  constructor(private root: HTMLElement, private cfg: ScanConfig) {}
  start() {
    this.renderCamera();
  }
  dispose() {
    this.stopDetect();
    stopStream();
  }

  private stopDetect() {
    this.camAlive = false;
    if (this.detectTimer) {
      clearTimeout(this.detectTimer);
      this.detectTimer = 0;
    }
  }

  private async renderCamera() {
    this.stopDetect();
    this.root.innerHTML = `
      <div class="camera-wrap" id="camwrap"><video id="cam" playsinline muted autoplay></video>
        <canvas class="cam-overlay" id="camoverlay"></canvas>
        <div class="cam-status" id="camstatus">Point at the sheet…</div></div>
      <p class="hint">${esc(this.cfg.hint ?? 'Hold the phone flat and square to the sheet. Capture when the border turns green.')}</p>
      <label class="btn btn-ghost btn-block" style="margin-top:10px">${icon.doc()} Use a photo instead
        <input type="file" accept="image/*" capture="environment" id="file" hidden></label>
      <div class="actionbar"><button class="btn btn-primary btn-block" data-capture>${icon.camera()} Capture &amp; grade</button></div>`;
    const video = qs<HTMLVideoElement>(this.root, '#cam')!;
    let haveCamera = false;
    let stream: MediaStream | null = null;
    onClick(this.root, '[data-capture]', async () => {
      if (!haveCamera) return toast('No camera — tap "Use a photo instead"', 'err');
      this.stopDetect();
      this.grade(await captureStill(video, stream));
    });
    qs<HTMLInputElement>(this.root, '#file')!.addEventListener('change', async (e) => {
      const f = (e.target as HTMLInputElement).files?.[0];
      if (f) {
        this.stopDetect();
        this.grade(await fileToImageData(f));
      }
    });
    try {
      stream = await getStream();
      video.srcObject = stream;
      await video.play().catch(() => {});
      haveCamera = true;
    } catch (err) {
      // Surface WHY the camera failed instead of a blanket "unavailable". The
      // error's name pinpoints the cause — essential for the wrapped-APK path,
      // which can't be reproduced in a desktop browser:
      //   NotAllowedError    → permission denied (native WebView bridge)
      //   NotFoundError      → no camera on the device
      //   NotReadableError   → camera held by another app
      //   OverconstrainedError → requested resolution unsupported
      //   TypeError/Security → insecure context / mediaDevices missing
      const name = (err as { name?: string })?.name || 'Error';
      const reason: Record<string, string> = {
        NotAllowedError: 'Camera permission was denied.',
        NotFoundError: 'No camera found on this device.',
        NotReadableError: 'The camera is being used by another app.',
        OverconstrainedError: "This camera can't provide the requested resolution.",
        SecurityError: 'Camera blocked — not a secure context.',
        TypeError: 'Camera API unavailable (insecure context or unsupported WebView).',
      };
      const msg = reason[name] ?? 'Camera unavailable.';
      const wrap = this.root.querySelector('.camera-wrap');
      if (wrap) {
        wrap.innerHTML = `<div class="center-col" style="color:#bbb">${icon.camera()}<p>${esc(msg)}<br>Use a photo instead.</p><p class="hint" style="font-size:11px;opacity:.55;margin-top:6px">details: ${esc(name)}</p></div>`;
      }
    }
    if (haveCamera) this.startDetect(video);
  }

  /** Throttled live loop: detect the sheet quad on a small preview frame and
   *  paint a green/red alignment overlay so the user knows when to capture. */
  private startDetect(video: HTMLVideoElement) {
    this.camAlive = true;
    const wrap = qs<HTMLDivElement>(this.root, '#camwrap')!;
    const overlay = qs<HTMLCanvasElement>(this.root, '#camoverlay')!;
    const status = qs<HTMLDivElement>(this.root, '#camstatus')!;
    const tick = async () => {
      if (!this.camAlive) return;
      if (!this.detectBusy && video.videoWidth) {
        this.detectBusy = true;
        try {
          const frame = frameToImageData(video, 720);
          const quad = await detectInWorker(frame);
          if (this.camAlive) this.paintGuide(wrap, overlay, status, video, quad);
        } catch {
          /* ignore transient detect errors */
        } finally {
          this.detectBusy = false;
        }
      }
      if (this.camAlive) this.detectTimer = window.setTimeout(tick, 250);
    };
    tick();
  }

  private paintGuide(
    wrap: HTMLDivElement,
    overlay: HTMLCanvasElement,
    status: HTMLDivElement,
    video: HTMLVideoElement,
    res: SheetQuad | null,
  ) {
    const W = wrap.clientWidth;
    const H = wrap.clientHeight;
    if (overlay.width !== W) overlay.width = W;
    if (overlay.height !== H) overlay.height = H;
    const ctx = overlay.getContext('2d')!;
    ctx.clearRect(0, 0, W, H);

    let state: 'good' | 'bad' = 'bad';
    let msg = 'Show all four corner squares';

    if (res && res.quad.length === 4) {
      // map preview-frame coords -> the letterboxed <video> display rect
      const vw = video.videoWidth || res.width;
      const vh = video.videoHeight || res.height;
      const scale = Math.min(W / vw, H / vh);
      const dispW = vw * scale;
      const dispH = vh * scale;
      const offX = (W - dispW) / 2;
      const offY = (H - dispH) / 2;
      const map = (p: { x: number; y: number }) => ({ x: offX + (p.x / res.width) * dispW, y: offY + (p.y / res.height) * dispH });
      const P = res.quad.map(map);

      const d = (a: { x: number; y: number }, b: { x: number; y: number }) => Math.hypot(a.x - b.x, a.y - b.y);
      const [tl, tr, br, bl] = res.quad;
      const top = d(tl, tr);
      const bottom = d(bl, br);
      const left = d(tl, bl);
      const right = d(tr, br);
      const skew = Math.max(Math.abs(top - bottom) / Math.max(top, bottom, 1), Math.abs(left - right) / Math.max(left, right, 1));
      const qw = Math.max(top, bottom) / res.width;
      const qh = Math.max(left, right) / res.height;
      const fillOK = qw > 0.45 && qh > 0.45;

      if (!fillOK) msg = 'Move closer — fill the frame';
      else if (skew > 0.13) msg = 'Hold the phone flat & square';
      else {
        state = 'good';
        msg = 'Aligned — capture now';
      }

      const color = state === 'good' ? '#39d353' : '#ff5c5c';
      // scrim everywhere except the detected sheet
      ctx.fillStyle = 'rgba(0,0,0,0.4)';
      ctx.beginPath();
      ctx.rect(0, 0, W, H);
      ctx.moveTo(P[0].x, P[0].y);
      for (let i = 1; i < 4; i++) ctx.lineTo(P[i].x, P[i].y);
      ctx.closePath();
      ctx.fill('evenodd');
      // quad outline
      ctx.strokeStyle = color;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(P[0].x, P[0].y);
      for (let i = 1; i < 4; i++) ctx.lineTo(P[i].x, P[i].y);
      ctx.closePath();
      ctx.stroke();
    }

    wrap.classList.toggle('good', state === 'good');
    wrap.classList.toggle('bad', state === 'bad');
    status.textContent = msg;
    status.className = 'cam-status ' + state;
  }

  private renderProcessing() {
    this.root.innerHTML = `<div class="center-col"><div class="spinner"></div><p>Reading sheet…</p>
      <p class="hint">First scan loads the vision module — a moment longer.</p></div>`;
  }

  private async grade(image: ImageData) {
    this.lastImage = image;
    toast(`Captured ${image.width}×${image.height}`);
    this.renderProcessing();
    try {
      const raw = await gradeInWorker(image, this.cfg.layout, { debug: true });
      if (!raw.ok) return this.renderError(raw.error ?? 'Could not read the sheet');
      this.renderReview(raw);
    } catch (e: any) {
      this.renderError(e?.message ?? String(e));
    }
  }

  private renderError(msg: string) {
    this.root.innerHTML = `<div class="card"><div class="badge badge-danger">${icon.alert()} Scan failed</div>
      <p style="margin:12px 0 0">${esc(msg)}</p>
      <p class="hint">Fill the frame with the whole sheet so all four corner squares show, keep it flat, and avoid glare/shadows.</p></div>
      <div class="stack">
        <button class="btn btn-ghost btn-block" data-sendphoto>${icon.doc()} Send this photo for diagnosis</button>
        <button class="btn btn-ghost btn-block" data-savephoto>${icon.download()} Save this photo</button>
      </div>
      <p class="hint">If it keeps failing, "Send for diagnosis" ships the exact frame back so the capture can be inspected.</p>
      <div class="actionbar"><button class="btn btn-primary btn-block" data-retake>${icon.refresh()} Try again</button></div>`;
    onClick(this.root, '[data-retake]', () => this.renderCamera());
    onClick(this.root, '[data-savephoto]', () => this.saveLastPhoto());
    onClick(this.root, '[data-sendphoto]', () => this.sendLastPhoto());
  }

  private lastBlob(cb: (blob: Blob | null) => void) {
    if (!this.lastImage) return cb(null);
    const canvas = document.createElement('canvas');
    drawImageData(canvas, this.lastImage);
    canvas.toBlob(cb, 'image/png');
  }

  /** POST the exact frame to the debug endpoint (falls back to a download). */
  private sendLastPhoto() {
    this.lastBlob(async (blob) => {
      if (!blob) return toast('No photo to send', 'err');
      try {
        const res = await fetch('/__debug_capture', { method: 'POST', headers: { 'content-type': 'image/png' }, body: blob });
        if (res.ok) return toast('Sent — thank you');
        throw new Error(String(res.status));
      } catch {
        toast('Could not send — saving instead', 'err');
        this.saveLastPhoto();
      }
    });
  }

  /** Download the exact frame the engine saw, for offline diagnosis. */
  private saveLastPhoto() {
    if (!this.lastImage) return toast('No photo to save', 'err');
    const canvas = document.createElement('canvas');
    drawImageData(canvas, this.lastImage);
    canvas.toBlob((blob) => {
      if (!blob) return toast('Could not export photo', 'err');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `omr-capture-${this.cfg.layout.quiz.items}items.png`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        URL.revokeObjectURL(a.href);
        a.remove();
      }, 150);
    }, 'image/png');
  }

  private renderReview(raw: RawResponse) {
    const opts = this.cfg.layout.quiz.options;
    const key = this.cfg.keyForScoring ?? null;
    const idRead = raw.studentNumber?.value ?? '';
    const matched = this.cfg.students?.find((s) => s.lrn === idRead && /^\d{12}$/.test(idRead));
    let assignedId = matched?.id ?? '';

    const flaggedCount = () => raw.answers.filter((a) => a.flags.length).length;
    const scoreNow = () => (key ? scoreResponse(raw.answers.map((a) => ({ q: a.q, chosen: a.chosen, flags: a.flags })), key) : null);

    const banner = () => {
      if (this.cfg.mode === 'key') {
        const n = flaggedCount();
        return n
          ? `<div class="card"><span class="badge badge-danger">${icon.alert()} ${n} flagged — confirm each before saving</span></div>`
          : `<div class="card"><span class="badge badge-ok">${icon.check()} No flags — ready to save as the key</span></div>`;
      }
      const sc = scoreNow();
      if (!sc) return `<div class="card"><span class="badge badge-warn">No answer key yet — stored for later scoring</span></div>`;
      return `<div class="score-banner" id="banner"><span class="big">${sc.score}/${sc.maxScore}</span>
        <span class="grow">${pct(sc.score, sc.maxScore)}%${sc.reviewItems.length ? ` · <span class="danger-text">${sc.reviewItems.length} to review</span>` : ''}</span></div>`;
    };

    const captured = this.cfg.mode === 'papers' && this.cfg.identificationMode === 'captured_name';
    const studentBlock = !(this.cfg.mode === 'papers')
      ? ''
      : captured
        ? `<div class="card"><div class="field" style="margin:0"><label for="cname">Student name <span class="muted">(read from the sheet)</span></label>
             ${raw.nameCrop ? `<div class="name-crop"><canvas id="namecrop"></canvas></div>` : ''}
             <input class="input" id="cname" autocomplete="off" placeholder="Type the name shown above" value=""></div></div>`
        : `<div class="card"><div class="field" style="margin:0"><label for="stu">Student ${matched ? '' : `<span class="danger-text">(read "${esc(idRead || 'blank')}" — assign)</span>`}</label>
             ${!matched && raw.nameCrop ? `<div class="name-crop"><canvas id="namecrop"></canvas></div><p class="hint">Couldn't read the LRN — identify the student from the written name.</p>` : ''}
             <select class="input" id="stu"><option value="">— unassigned —</option>${(this.cfg.students ?? [])
               .map((s) => `<option value="${esc(s.id)}" ${s.id === assignedId ? 'selected' : ''}>${esc(s.name)} (${esc(s.lrn)})</option>`)
               .join('')}</select></div></div>`;

    this.root.innerHTML = `
      ${raw.debugImage ? `<div class="scan-preview"><canvas id="ov"></canvas></div>` : ''}
      <div id="banner-slot">${banner()}</div>
      ${studentBlock}
      <p class="section-label">Answers · tap to correct ${flaggedCount() ? `· <span class="danger-text">${flaggedCount()} flagged</span>` : ''}</p>
      <div class="answers-grid" id="grid">${answerGridHtml(raw.answers, opts, key)}</div>
      <div class="actionbar">
        <button class="btn btn-ghost" data-retake>${icon.refresh()} Retake</button>
        <button class="btn btn-primary" data-save ${this.cfg.mode === 'key' && flaggedCount() ? 'disabled' : ''}>${icon.check()} Save</button>
      </div>`;

    // The preview carries the same outcome badges as the grid, so the sheet can
    // be read straight off the photo. Redrawn on every correction (below) so the
    // two never disagree.
    const ovCanvas = qs<HTMLCanvasElement>(this.root, '#ov');
    const paintPreview = () => {
      if (ovCanvas && raw.debugImage) {
        drawSheetPreview(ovCanvas, raw.debugImage, raw.meta.contentRect, this.cfg.layout, raw.answers, key);
      }
    };
    paintPreview();
    // The name crop is shown for captured_name mode AND for a roster scan whose
    // LRN didn't auto-match. Draw only when the canvas is actually present — it
    // isn't in answer-key mode (no identity there), where `!matched` is also true.
    const nameCanvas = qs<HTMLCanvasElement>(this.root, '#namecrop');
    if (nameCanvas && raw.nameCrop) drawImageData(nameCanvas, raw.nameCrop);
    const stu = qs<HTMLSelectElement>(this.root, '#stu');
    stu?.addEventListener('change', () => (assignedId = stu.value));

    qs(this.root, '#grid')!.addEventListener('click', (e) => {
      const el = (e.target as HTMLElement).closest('.opt') as HTMLElement | null;
      if (!el) return;
      const q = Number(el.dataset.q);
      const item = raw.answers.find((a) => a.q === q)!;
      item.chosen = item.chosen === el.dataset.opt ? null : el.dataset.opt!;
      item.flags = []; // a manual correction resolves the ambiguity
      const row = qs(this.root, `.qrow[data-row="${q}"]`);
      if (row) row.outerHTML = answerRowHtml(item, opts, key);
      paintPreview();
      qs(this.root, '#banner-slot')!.innerHTML = banner();
      const saveBtn = qs<HTMLButtonElement>(this.root, '[data-save]');
      if (saveBtn && this.cfg.mode === 'key') saveBtn.disabled = flaggedCount() > 0;
    });

    onClick(this.root, '[data-retake]', () => this.renderCamera());
    onClick(this.root, '[data-save]', async () => {
      if (this.cfg.mode === 'key' && flaggedCount()) return toast('Confirm all flagged items first', 'err');
      if (captured) {
        const name = qs<HTMLInputElement>(this.root, '#cname')!.value.trim();
        if (!name) return toast('Type the student name first', 'err');
        const img = raw.nameCrop ? await imageDataToPngBlob(raw.nameCrop) : null;
        await this.cfg.onSave(raw, { studentId: null, capturedName: name, capturedNameImage: img });
      } else {
        // Roster scan: when the LRN didn't auto-match, keep a crop of the name box
        // so the result can be identified later in Review. (Not in key mode, which
        // has no identity and no #namecrop element.)
        const nameImg = this.cfg.mode === 'papers' && !matched && raw.nameCrop ? await imageDataToPngBlob(raw.nameCrop) : null;
        await this.cfg.onSave(raw, { studentId: assignedId || null, capturedNameImage: nameImg });
      }
    });
  }
}
